import React, { useEffect, useState } from 'react';
import podiumService from '../../services/podiumService';

const GOLD = 'rgb(var(--brand-gold-rgb))';
const mmss = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// The review queue: everything the machine declined to decide. Each item is one decision — confirm with a name (an
// existing person or a new one) or reject. Authority order: cue > fingerprint > content; the human outranks all three.
export default function PodiumReviewPanel() {
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState({});
  const [msg, setMsg] = useState(null);
  const load = () => Promise.all([podiumService.getReview('open'), podiumService.getPeople()])
    .then(([r, p]) => { setItems(r.data.items || []); setPeople(p.data.people || []); }).catch(() => setMsg('could not load the queue'));
  useEffect(() => { load(); }, []);
  const act = (it, action) => {
    const d = draft[it.id] || {};
    const data = it.kind === 'spelling' ? { name: d.name || '', firm: d.firm || '' }
      : { person_key: d.person_key || '', name: d.name || '', role_hint: d.role_hint || 'executive', firm: d.firm || '' };
    setBusy(it.id);
    podiumService.resolveReview(it.id, action, data).then(() => { setMsg(`${action}ed #${it.id}`); load(); })
      .catch(e => setMsg(e?.response?.data?.error || 'failed')).finally(() => setBusy(null));
  };
  const set = (id, k, v) => setDraft(s => ({ ...s, [id]: { ...(s[id] || {}), [k]: v } }));
  if (!items.length) return <p className="text-sm text-gray-500 dark:text-gray-400">{msg || 'The queue is empty.'}</p>;
  return (
    <div className="space-y-3">
      {msg && <p className="text-xs text-gray-400">{msg}</p>}
      {items.map(it => {
        const p = it.payload || {}; const d = draft[it.id] || {};
        return (
          <div key={it.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 text-sm space-y-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: GOLD }}>{it.kind.replace('_', ' ')}</span>
              <span className="font-medium">{it.ticker} · {it.call}</span>
              <span className="text-xs text-gray-400">{it.held_at ? it.held_at.slice(0, 10) : ''}</span>
            </div>
            <div className="text-gray-800 dark:text-gray-200">
              {it.kind === 'unnamed_voice' && <>Voice <b>{p.voice}</b>{p.minutes ? ` spoke ${p.minutes} min` : ''} and nobody introduced it.{p.candidates?.length ? <> Introduced on the call but never cued: <b>{p.candidates.join(', ')}</b>.</> : null}</>}
              {it.kind === 'spelling' && <>Heard as <b>{p.heard}</b> from the operator's hand-off; the person is certain, the spelling is not.</>}
              {it.kind === 'alias' && <>Heard as <b>{p.heard}</b>, voice matches <b>{p.same_as}</b> (cos {p.cos}).</>}
              {it.kind === 'contradiction' && <>Voice <b>{p.voice}</b>: the fingerprint and the cues disagree.</>}
              {(p.clips || []).length > 0 && <span className="ml-2 text-xs text-gray-400">clips {(p.clips || []).slice(0, 3).map(c => mmss(c[0])).join(', ')}</span>}
            </div>
            {(p.evidence || []).slice(0, 2).map((e, i) => <div key={i} className="text-xs text-gray-400">{e.cue}</div>)}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {it.kind !== 'spelling' && (
                <select value={d.person_key || ''} onChange={e => set(it.id, 'person_key', e.target.value)} className="rounded border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700">
                  <option value="">— existing person —</option>
                  {people.map(x => <option key={x.key} value={x.key}>{x.name}{x.firm ? ` (${x.firm})` : ''}</option>)}
                </select>)}
              <input placeholder={it.kind === 'spelling' ? 'correct name' : 'or a new name'} value={d.name || ''} onChange={e => set(it.id, 'name', e.target.value)}
                className="rounded border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700" />
              <input placeholder="firm / company" value={d.firm || ''} onChange={e => set(it.id, 'firm', e.target.value)}
                className="rounded border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700 w-36" />
              {it.kind !== 'spelling' && (
                <select value={d.role_hint || 'executive'} onChange={e => set(it.id, 'role_hint', e.target.value)} className="rounded border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700">
                  {['executive', 'host', 'analyst', 'operator'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>)}
              <button disabled={busy === it.id} onClick={() => act(it, 'confirm')} className="rounded border px-3 py-1 text-xs uppercase tracking-[0.15em]" style={{ color: GOLD, borderColor: GOLD }}>Confirm</button>
              <button disabled={busy === it.id} onClick={() => act(it, 'reject')} className="rounded border border-gray-300 px-3 py-1 text-xs uppercase tracking-[0.15em] text-gray-400 dark:border-gray-700">Reject</button>
            </div>
          </div>);
      })}
    </div>
  );
}
